import {useRecoilState} from 'recoil';
import {currentGroupState, groupsState} from '../states/groups';
import {getTicketGroup, getListCodeGroups, getGroupGuests} from '../external_usage';
import {useEffect} from 'react';
import {editingGuestState, editingGuestStateT, guestsState, sendTicketState} from '../states/tickets';
import {useLocation} from 'react-router-dom';
import {exist} from '../helpers/common';
import {parseTicketsForView, uniFieldsGuests} from '../helpers/guests';

export const useGetGroupsList = () => {
  const [groups, setState] = useRecoilState(groupsState);

  const getGroupList = async () => {
    setState((prev) => ({...prev, loading: true}));
    const res = await getListCodeGroups();
    setState((prev) => {
      return {...prev, groups: res.body, loading: false};
    });
  };
  return {...groups, getGroupList};
};

export const useGetCurrentGroup = () => {
  const [currentGroup, setState] = useRecoilState(currentGroupState);

  const getGroup = async (id: string | number) => {
    if (id === undefined) return;
    setState((prev) => ({...prev, loading: true}));
    const res = await getTicketGroup({groupId: String(id)});
    setState((prev) => {
      return {...prev, group: res.body, loading: false};
    });
  };

  const refetch = async () => {
    const id = currentGroup?.group?.id;
    if (!id) return;
    const res = await getTicketGroup({groupId: String(id)});
    setState((prev) => {
      return {...prev, group: res.body, loading: false};
    });
  };
  return {currentGroup, getGroup, refetch};
};

export const useCurrentGroupFromUrlParam = () => {
  const {search} = useLocation();
  const {
    currentGroup: {group},
    getGroup,
  } = useGetCurrentGroup();

  const groupId = new URLSearchParams(search).get('groupId');
  const alreadyExist = exist(group?.id) && String(group?.id) === groupId;

  useEffect(() => {
    if (!alreadyExist && groupId && exist(groupId)) getGroup(groupId);
  }, []);
};

export const useSetInitialCurrentGroup = () => {
  const [{groups}] = useRecoilState(groupsState);
  const [{group}] = useRecoilState(currentGroupState);
  const {getGroup} = useGetCurrentGroup();

  useEffect(() => {
    if (!group && groups) {
      getGroup(groups[0]?.id);
    }
  }, [!!groups, !!group]);
};

export const useChangeSendTicketState = () => {
  const [state, setState] = useRecoilState(sendTicketState);
  const changeName = (value?: any) => setState((prev) => ({...prev, name: value}));
  const changeEmail = (value?: any) => setState((prev) => ({...prev, email: value}));
  const changeQuantity = (id: string, value?: any) =>
    setState((prev) => ({...prev, quantity: {...(prev?.quantity || {}), [id]: value}}));

  const setError = (error?: string) => setState((prev) => ({...prev, error: error}));
  const clear = () => setState({});
  return {state, changeName, changeEmail, changeQuantity, setError, clear};
};

export const useGuestsState = () => {
  const {
    currentGroup: {group},
  } = useGetCurrentGroup();
  const [state, setState] = useRecoilState(guestsState);

  const getGuestList = async (groupId?: string | number) => {
    if (!exist(groupId)) return;
    try {
      setState((prev) => ({...prev, loading: true}));
    } catch (error) {
      console.error(error);
    } finally {
      setState((prev) => ({...prev, loading: false}));
    }
    const res = await getGroupGuests({groupId: String(groupId)});
    const guests = uniFieldsGuests(res?.body?.guests, res?.body?.ticketGroupGuests) || [];
    const modified = parseTicketsForView(guests, group);
    setState((prev) => ({...prev, guests: modified}));
  };

  const refetchGuestList = async () => {
    const groupId = group?.id;
    if (!groupId) return;

    try {
      const res = await getGroupGuests({groupId: String(groupId)});
      const guests = uniFieldsGuests(res?.body?.guests, res?.body?.codeGroupGuests) || [];
      const modified = parseTicketsForView(guests, group);
      setState((prev) => ({...prev, guests: modified}));
    } catch (error) {
      console.error(error);
    }
  };

  return {guests: state?.guests, loading: state?.loading, getGuestList, refetchGuestList};
};

export const useEditingGuestState = () => {
  const [state, setState] = useRecoilState(editingGuestState);
  const setId = (value?: any) => setState((prev) => ({...prev, guestId: value}));
  const changeName = (value?: any) => setState((prev) => ({...prev, name: value}));
  const changeEmail = (value?: any) => setState((prev) => ({...prev, email: value}));
  const changeQuantity = (id: string, value?: any) =>
    setState((prev) => ({...prev, quantity: {...(prev?.quantity || {}), [id]: value}}));

  const setInitial = (value: editingGuestStateT) => setState(value);
  const setError = (error?: string) => setState((prev) => ({...prev, error: error}));
  const clear = () => setState({});
  return {state, changeName, changeEmail, changeQuantity, setId, setError, setInitial, clear};
};
